import { createGroup, editGroup } from '@/api/groups';
import countrySelectNormalizer from '@/components/app/form/select/normalizers/countrySelectNormalizer';
import mapPublicationApiErrorsToFormikErrors from '@/components/app/publications/mapPublicationApiErrorsToFormikErrors';
import useScrollToError from '@/form/useScrollToError';
import { useIsWorldOrganiser } from '@/hooks/user';
import { Group } from '@/types/core/Group';
import { CreateGroupDTO } from '@/types/dto/CreateGroupDTO';
import { Button, Flex, ModalBody, useToast } from '@chakra-ui/react';
import TextArea from '@components/app/form/TextArea';
import TextInput from '@components/app/form/TextInput';
import CountryCombobox from '@components/app/form/select/CountryCombobox';
import FormikDownshiftSelect from '@components/app/form/select/FormikDownshiftSelect';
import SwitchForm from '@components/app/form/select/SwitchForm';
import { ComboboxItem } from '@components/app/form/select/downshift/types';
import ModalFooter from '@components/presentational/CustomModal/ModalFooter';
import { Formik, FormikProps } from 'formik';

interface FormValues {
  name: string;
  private: boolean;
  goal: string;
  target: string;
  country: ComboboxItem | null;
}

function FormikForm({
  handleSubmit,
  values,
  submitCount,
  isValid,
  isValidating,
  isSubmitting,
  errors,
}: FormikProps<FormValues>) {
  useScrollToError(submitCount, isValid, isValidating, isSubmitting, errors);

  const isWorldOrganiser = useIsWorldOrganiser();

  return (
    <>
      <ModalBody>
        <form
          id={'groupForm'}
          onSubmit={evt => {
            evt.preventDefault();
            handleSubmit(evt);
          }}
          noValidate
        >
          <Flex direction="column" gap="1rem">
            <TextInput name={'name'} label={'Titre'} required />
            {isWorldOrganiser && (
              <FormikDownshiftSelect
                label={'Pays'}
                name={'country'}
                accentColor={'tournesol'}
                SelectComponent={CountryCombobox}
                id={'country'}
                placeholder={'Sélectionnez mon pays'}
                fields={{ country: 'name,code' }}
                orders={['name']}
              />
            )}

            <TextArea name={'goal'} label={'Objectif(s) du groupe'} required />
            <TextInput name={'target'} label={'Public visé'} required />
            <SwitchForm
              name={'private'}
              label={'Groupe privé'}
              isInline={true}
            />
          </Flex>
        </form>
      </ModalBody>
      <ModalFooter
        borderWidth="1px 0 0 0"
        borderStyle="solid"
        borderColor="black.50"
      >
        <Button
          size="sm"
          variant="sunflower"
          w="fit-content"
          type={'submit'}
          form={'groupForm'}
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
          cursor={isSubmitting ? 'not-allowed' : 'pointer'}
        >
          Valider
        </Button>
      </ModalFooter>
    </>
  );
}

function normalizeFormData(values: FormValues): CreateGroupDTO {
  return {
    name: values.name,
    private: values.private,
    goal: values.goal,
    target: values.target,
    countryId: values.country ? values.country.id : null,
  };
}

export default function GroupForm({
  group,
  onSubmitEnd,
}: {
  group?: Group;
  onSubmitEnd?: (idGroup?: string) => void;
}) {
  const toast = useToast();

  const initialValues: FormValues = {
    name: group ? group.name : '',
    private: group ? group.private : false,
    goal: group ? group.goal : '',
    target: group ? group.target : '',
    country: group?.country ? countrySelectNormalizer(group.country) : null,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, actions) => {
        let response;
        try {
          if (group?.id) {
            response = await editGroup(group.id, normalizeFormData(values));
          } else {
            response = await createGroup(normalizeFormData(values));
          }
        } catch (err) {
          toast({
            title: 'Erreur',
            description: 'Une erreur est survenue',
            status: 'error',
            duration: 9000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          return;
        }

        if (response.ok && onSubmitEnd) {
          const data: { data: { id?: string } } = await response.json();
          onSubmitEnd(data.data?.id);
        }

        if (
          response.status === 400 &&
          response.headers.get('Content-Type') === 'json+errors'
        ) {
          const formErrors = await response.json();

          actions.setErrors(mapPublicationApiErrorsToFormikErrors(formErrors));
        }
        actions.setSubmitting(false);
      }}
    >
      {formProps => {
        return <FormikForm {...formProps} />;
      }}
    </Formik>
  );
}
